/**
 * Chiamate alle API degli specialisti della piattaforma (ADR 0059 §10, fase 4).
 *
 * L'analisi la chiede l'Institore del set: la pagina del progetto gli manda un messaggio
 * normale (`AgentsApi.sendMessage`). Qui ci sono solo le letture della pagina: le analisi
 * recenti di un progetto e il rapporto HTML, che si scarica come file e non si apre mai
 * sull'origine della dashboard.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3), presidiati da
 * `specialists.test.ts`.
 */

import type { ApiClient } from './client';

/** Stato di un'analisi. */
export type SpecialistRunStatus =
  | 'queued'
  | 'starting'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'expired';

/** Stati in cui l'analisi non è ancora finita. */
export const ACTIVE_RUN_STATUSES: readonly SpecialistRunStatus[] = ['queued', 'starting', 'running'];

/** Un'analisi o una domanda successiva su un progetto. */
export interface SpecialistRun {
  /** Identificativo dell'analisi. */
  id: string;
  /** Specialista: `seo`. */
  kind: string;
  /** Analisi a cui la domanda si riferisce; `null` per un'analisi. */
  parent_id: string | null;
  /** Ambiente analizzato. */
  environment: 'prod' | 'demo';
  /** Tutto il sito o una pagina. */
  scope: 'site' | 'page';
  /** Percorso della pagina, con `scope: page`. */
  page: string;
  /** Domanda dell'utente, se c'era. */
  question: string;
  /** Stato. */
  status: SpecialistRunStatus;
  /** Motivo di un'analisi fallita o scaduta. */
  reason: string;
  /** Punteggio 0-100 del riassunto; `null` se non misurato o non ancora arrivato. */
  score: number | null;
  /** Problemi critici del riassunto. */
  critical: number;
  /** Problemi di priorità alta. */
  high: number;
  /** Token conteggiati. */
  tokens: number;
  /** True se il rapporto si può scaricare (7 giorni dall'analisi). */
  has_report: boolean;
  /** Richiesta, ISO 8601. */
  created_at: string;
  /** Fine, ISO 8601; `null` se in corso. */
  finished_at: string | null;
  /** Fine delle domande successive, ISO 8601; `null` se non ci sono. */
  session_expires_at: string | null;
}

/** Risposta dell'elenco. */
interface SpecialistRunsPage {
  /** Analisi, dalla più recente. */
  items: SpecialistRun[];
}

/** Client delle letture degli specialisti. */
export class SpecialistsApi {
  /**
   * @param client Client HTTP della sessione.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Analisi recenti di un progetto in un set.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param kind Specialista (`seo` per la sezione «Analisi SEO»; ADR 0060 aggiunge
   *   `competitors`, le cui analisi non devono comparire lì).
   * @returns Le analisi, dalla più recente.
   */
  async listRuns(setId: string, projectId: string, kind: 'seo' | 'competitors' = 'seo'): Promise<SpecialistRun[]> {
    const page = await this.client.request<SpecialistRunsPage>(
      'GET',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/specialist-runs?kind=${kind}`,
    );
    return page.items;
  }

  /**
   * Rapporto HTML di un'analisi, come file da salvare.
   *
   * @param setId Set.
   * @param runId Analisi.
   * @returns Il contenuto.
   */
  downloadReport(setId: string, runId: string): Promise<Blob> {
    return this.client.requestBlob(
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/specialist-runs/${encodeURIComponent(runId)}/report`,
    );
  }
}
